/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 *
 */

/**
 * @module bulb-attached-file/bulbattachedfilecommand
 */

import {
  Command, type ModelWriter, type ModelElement, ModelSelectable,
} from 'ckeditor5';

/**
 * The bulb attached file command, the actual insert capabilities of the plugin.
 *
 * @extends Command
 */
export class InsertBulbAttachedFileCommand extends Command {

  /**
   * Method for creating attach file element.
   *
   * @param writer
   * @param blockDetails Contains id and url for attach file block.
   *
   */
  static createAttachedFileBlock(writer: ModelWriter, blockDetails: { id: any; version: any; }) {
    return writer.createElement('attachedFile', {
      'bulb-block-id': blockDetails.id,
      'bulb-block-version': blockDetails.version,
    });
  }

  /**
   * @inheritDoc
   *
   * @param blockDetails Contains id and url for attach file block.
   * @param placeholderElement The Placeholder element.
   * @param insertPosition Position of the block as per the placeholder.
   */
  public override execute(blockDetails, placeholderElement: ModelElement, insertPosition: ModelSelectable): void {
    this.editor.model.change((writer: ModelWriter) => {
      // Check for an insertPosition, this would only happen if we had to upload the asset and had the uploading
      // placeholder and need to put this exactly where the placeholder was.
      if (insertPosition) {
        this.editor.model.insertContent(
          InsertBulbAttachedFileCommand.createAttachedFileBlock(writer, blockDetails),
          insertPosition,
        );
      } else {
        this.editor.model.insertContent(InsertBulbAttachedFileCommand.createAttachedFileBlock(writer, blockDetails));
      }

      CKEBulbData.service.ckeStateService.handleSaveOperation.next(true);
      CKEBulbData.service.ckeStateService.insertParagraphSubject.next(true);

      if (placeholderElement) {
        writer.remove(placeholderElement);
      }
    });
  }

  /**
   * @inheritDoc
   */
  public override refresh(): void {
    const { model } = this.editor;
    const { selection } = model.document;
    const allowedIn = model.schema.findAllowedParent(selection.getFirstPosition(), 'attachedFile');

    this.isEnabled = allowedIn !== null;
  }
}
